import { useEffect } from 'react';
import { Accordion, Box, Heading, Paragraph, Text } from '@zillow/constellation-10';
import type { CreditReportType } from '@zg-rentals/ts-capps-client';
import { formatDate } from '@zg-rentals/formatters';
import {
  getAverageAgeOpenAccounts,
  getCreditAccounts,
  getOldestAccountAge,
  type PaymentAccount,
} from '@zg-rentals/applications-utils';
import { countReportData } from '../../utils/report-utils';
import type { EmitMetric } from '../../models/report-models';
import { ReportType } from '../../models/report-models';

export function CreditReportAccounts({
  creditReport,
  emitMetric,
}: {
  creditReport: CreditReportType;
  emitMetric: EmitMetric;
}) {
  const { openAccounts, closedAccounts } = getCreditAccounts(creditReport);
  const averageAgeOpenAccounts = getAverageAgeOpenAccounts(creditReport);
  const oldestAccountAge = getOldestAccountAge(creditReport);
  const hasAccounts = openAccounts.length > 0 || closedAccounts.length > 0;

  useEffect(() => {
    countReportData(ReportType.CreditReport, 'openAccounts', openAccounts?.length, emitMetric);
    countReportData(ReportType.CreditReport, 'closedAccounts', closedAccounts?.length, emitMetric);
  }, []);

  return (
    <>
      <Box css={{ marginBottom: 'sm' }}>
        <Heading level={3} textStyle="heading-xs" css={{ marginTop: 'md' }}>
          Account overview
        </Heading>
        {hasAccounts && <Paragraph textStyle="body-xs">Data displayed is from the last 25 months</Paragraph>}
      </Box>

      {hasAccounts ? (
        <>
          {openAccounts.length > 0 && (
            <>
              <Heading level={4} textStyle="body-lg-bold" css={{ marginBottom: 'sm' }}>
                Open accounts
              </Heading>
              <AccountAccordion accounts={openAccounts} />
              <Box css={{ display: 'flex', justifyContent: 'center', marginTop: 'xs', marginBottom: 'xs' }}>
                <Text textStyle="body-sm">
                  Average age of open accounts: {averageAgeOpenAccounts ? averageAgeOpenAccounts : 'Unavailable'}
                </Text>
              </Box>
            </>
          )}
          {closedAccounts.length > 0 && (
            <>
              <Heading level={4} textStyle="body-lg-bold" css={{ marginBottom: 'sm' }}>
                Closed accounts
              </Heading>
              <AccountAccordion accounts={closedAccounts} />
            </>
          )}
          {oldestAccountAge && (
            <Box css={{ display: 'flex', justifyContent: 'center', marginTop: 'xs' }}>
              <Text textStyle="body-sm">Age of oldest account: {oldestAccountAge}</Text>
            </Box>
          )}
        </>
      ) : (
        <Paragraph textStyle="body-sm">None found</Paragraph>
      )}
    </>
  );
}

function AccountAccordion({ accounts }: { accounts: Array<PaymentAccount> }) {
  return (
    <Accordion.Root>
      {accounts.map((account, index) => (
        <Accordion.Item key={index} value={`${index}`}>
          <Accordion.Header>
            <Paragraph>{account.name}</Paragraph>
            <Paragraph>
              <strong>{account.amount}</strong>
            </Paragraph>
          </Accordion.Header>
          <Accordion.Panel>
            <PaymentAccountContent account={account} />
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion.Root>
  );
}

const PaymentAccountContent = ({ account }: { account: PaymentAccount }) => (
  <>
    <AccountValue label="Balance" value={account.amount} />
    <AccountValue label="Open date" value={formatDate(account.openDate, 'numbers')} />
    <AccountValue label="On-time payments" value={account.onTimePayments} />
    <AccountValue label="Late payments" value={account.latePayments} />
    <AccountValue label="Last late payment" value={formatDate(account.lastLatePayment, 'numbers')} />
    {account.accountComment && <AccountValue label="Comment" value={account.accountComment} />}
  </>
);

function AccountValue({ label, value }: { label: string; value: string }) {
  return (
    <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', marginBottom: 'xs', gap: 'xs' }}>
      <Box css={{ display: 'grid', gridTemplateColumns: { base: 'span 1', smDown: 'span 2' } }}>
        <Text textStyle="body-sm">{label}</Text>
      </Box>
      <Box css={{ display: 'grid', gridTemplateColumns: { base: 'span 4', smDown: 'span 3' } }}>
        <Text textStyle="body-sm-bold">{value}</Text>
      </Box>
    </Box>
  );
}
